// Response-sets table columns — the domain cells (set identity, tinted response
// chips, option count, last-updated datetime, last-updated-by avatar, row
// kebab) rendered inside the common DataTable in FormResponsesModal.
//
// The kebab's three actions are the same flows the modal's selection bar runs;
// they arrive here as callbacks so a row menu and a one-row selection can't
// diverge.

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Icon, type DataTableColumn } from '@/components/common';
import ResponseSetRowActions from './ResponseSetRowActions';
import type { FormPerson } from '@/api/endpoints/formsApi';
import type { ResponseSet } from '@/api/endpoints/formChoicesApi';
import i18n from '@/i18n/config';
import initialsOf from '@/utils/initials';
import toneOf from '@/utils/avatarTone';

// Deterministic per-person avatar tone so the same person is always one color.

// Tinted chip style from a choice's 8-digit hex (#rrggbbaa); alpha 00 / short
// hex → neutral tokens (same idiom as the builder's FieldPreview chips).
const chipStyle = (color: string): CSSProperties | undefined => {
  const h = color?.startsWith('#') ? color.slice(1) : '';
  if (h.length < 6 || (h.length === 8 && h.slice(6, 8).toLowerCase() === '00')) return undefined;
  const rgb = `#${h.slice(0, 6)}`;
  return { background: `${rgb}26`, color: rgb };
};

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
};

const PersonCell = ({ person }: { person: FormPerson | null }) =>
  person ? (
    <span className='forms-table__person'>
      <Avatar
        initials={initialsOf(person.fullName)}
        size={28}
        tone={toneOf(person.id || person.fullName)}
      />
      <span className='forms-table__person-name'>{person.fullName}</span>
    </span>
  ) : (
    <span className='forms-table__muted'>—</span>
  );

interface UseResponseSetColumnsArgs {
  onEdit: (row: ResponseSet) => void;
  onDuplicate: (row: ResponseSet) => void;
  onDelete: (row: ResponseSet) => void;
}

export const useResponseSetColumns = ({
  onEdit,
  onDuplicate,
  onDelete,
}: UseResponseSetColumnsArgs): DataTableColumn<ResponseSet>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'name',
      label: t('forms_responses_col_name'),
      width: 'minmax(12rem, 1.4fr)',
      sortable: true,
      render: (r) => (
        <span className='forms-table__name'>
          <span className='forms-table__icon' aria-hidden='true'>
            <Icon name='list-checks' size={16} />
          </span>
          <span className='forms-table__name-text'>{r.name}</span>
        </span>
      ),
    },
    {
      key: 'responses',
      label: t('forms_responses_col_responses'),
      width: 'minmax(14rem, 2fr)',
      render: (r) => (
        <span className='form-responses__chips'>
          {r.choices.map((c, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i} className='form-responses__chip' style={chipStyle(c.color)}>
              {c.label || '—'}
            </span>
          ))}
        </span>
      ),
    },
    {
      key: 'options',
      label: t('forms_responses_col_options'),
      width: '6rem',
      sortable: true,
      render: (r) => <span className='forms-table__entries'>{r.choices.length}</span>,
    },
    {
      key: 'updated',
      label: t('table_header_last_updated'),
      width: 'minmax(11rem, 1fr)',
      sortable: true,
      render: (r) => <span className='forms-table__muted'>{fmtDateTime(r.updatedAt)}</span>,
    },
    {
      key: 'by',
      label: t('table_header_updated_by'),
      width: 'minmax(11rem, 1fr)',
      render: (r) => <PersonCell person={r.updatedBy} />,
    },
    {
      key: 'actions',
      label: '',
      width: '3.5rem',
      render: (r) => (
        <ResponseSetRowActions
          onEdit={() => onEdit(r)}
          onDuplicate={() => onDuplicate(r)}
          onDelete={() => onDelete(r)}
        />
      ),
    },
  ];
};
